import React, { useCallback } from 'react';
import { GridCallbackDetails } from '@mui/x-data-grid';

export interface PaginationValueProps {
  page?: number;
  pageSize?: number;
}

export interface QueryValueProps {
  pageNumber?: number;
  pageSize?: number;
}

interface PaginationHandlerProps {
  onPageChange?: (page: number, details?: GridCallbackDetails<any>) => void;
  onPageSizeChange?: (
    pageSize: number,
    details: GridCallbackDetails<any>
  ) => void;
}

export type PaginationProps = PaginationValueProps & PaginationHandlerProps;

export interface PaginationOptions<T = QueryValueProps> {
  defaultValues?: PaginationValueProps;
  mapQueryParams?: (paginationParams: PaginationValueProps) => T;
}

export interface PaginationResult<T = QueryValueProps> {
  paginationProps: PaginationProps;
  queryProps: T;
}

const defaultPaginationValues: PaginationValueProps = {
  page: 0,
  pageSize: 25
};

const defaultMapQueryParams = ({
  page,
  pageSize
}: PaginationValueProps): any => ({
  pageNumber: page + 1,
  pageSize
});

const usePagination = <T = QueryValueProps>({
  defaultValues = {},
  mapQueryParams = defaultMapQueryParams
}: PaginationOptions<T> = {}): PaginationResult<T> => {
  const [paginationParams, setPaginationParams] =
    React.useState<PaginationValueProps>({
      ...defaultPaginationValues,
      ...defaultValues
    });

  const onPageSizeChange = useCallback(
    (pageSize: number) =>
      setPaginationParams((p) => ({ ...p, pageSize, page: 0 })),
    []
  );

  const onPageChange = useCallback(
    (page: number) => setPaginationParams((p) => ({ ...p, page })),
    []
  );

  return {
    paginationProps: {
      ...paginationParams,
      onPageChange,
      onPageSizeChange
    },
    queryProps: mapQueryParams(paginationParams)
  };
};

export default usePagination;
